/**
 * Technical Report Viewer Component
 *
 * Professional technical interface for EUDR compliance reports
 * designed for expert users in satellite imagery analysis and forestry monitoring.
 *
 * Features:
 * - NDVI satellite imagery visualization (Baseline/Monitoring/Change Detection)
 * - Detailed analysis parameters (Sentinel-2, spectral bands, resolution)
 * - Quantitative statistical metrics
 * - Compliance threshold analysis
 * - Technical metadata display
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Satellite,
    Activity,
    BarChart3,
    FileText,
    Download,
    ExternalLink,
    Info,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Calendar,
    MapPin,
    Layers,
    Zap,
    TreePine,
    Lock,
    Shield,
    Loader2,
} from 'lucide-react';
import { reportsApi, analysisApi, validationApi, getErrorMessage } from '../../lib/api';

interface TechnicalReportViewerProps {
    reportId: string;
}

interface AnalysisParams {
    satellite_platform: string;
    spatial_resolution: string;
    spectral_bands: string[];
    baseline_period: string;
    monitoring_period: string;
    analysis_method: string;
    cloud_cover_filter: string;
    ndvi_threshold: number;
    deforestation_threshold: number;
}

interface StatisticalMetrics {
    total_area_ha: number;
    stable_forest_ha: number;
    detected_loss_ha: number;
    loss_rate_percent: number;
    compliance_threshold_ha: number;
    margin_to_threshold_ha: number;
}

interface SatelliteImagery {
    baseline: {
        url: string;
        date: string;
        cloud_cover: number;
        scene_id: string;
    };
    monitoring: {
        url: string;
        date: string;
        cloud_cover: number;
        scene_id: string;
    };
    change_detection: {
        url: string;
        alerts_count: number;
        largest_patch_ha: number;
    };
}

interface BaselineState {
    classification: 'FOREST' | 'AGRICULTURE' | 'GRASSLAND';
    forest_cover_percentage: number;
    is_locked: boolean;
    reference_date: string;
    confidence: number;
    data_source: string;
}

interface FAOThresholds {
    tree_cover_density: {
        threshold: number;
        current_value: number;
        compliant: boolean;
    };
    minimum_area: {
        threshold_ha: number;
        current_ha: number;
        compliant: boolean;
    };
    tree_height: {
        threshold_m: number;
        estimated_m: number;
        compliant: boolean;
        method: string;
    };
}

interface EdgeEffects {
    original_area_ha: number;
    buffer_distance_m: number;
    core_area_ha: number;
    edge_percentage: number;
}

export default function TechnicalReportViewer({ reportId }: TechnicalReportViewerProps) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'imagery' | 'statistics' | 'eudr' | 'parameters' | 'metadata'>('imagery');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<any>(null);
    const [snapshotData, setSnapshotData] = useState<any>(null);
    const [analysisData, setAnalysisData] = useState<any>(null);
    const [validationData, setValidationData] = useState<any>(null);
    const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});

    // Fetch report data — prefer frozen snapshot over live analysis
    useEffect(() => {
        const fetchReportData = async () => {
            try {
                setLoading(true);
                setError(null);

                // 1. Get report metadata
                const report = await reportsApi.get(reportId);
                setReportData(report);

                // 2. Try to load frozen snapshot first (Phase 4 MVP)
                let snapshot = null;
                try {
                    snapshot = await reportsApi.getSnapshot(reportId);
                    setSnapshotData(snapshot);
                } catch {
                    console.warn('No snapshot available, falling back to live data');
                }

                // 3. Get live analysis data (for imagery + enrichment)
                if (report.analysis_job_id) {
                    try {
                        const analysis = await analysisApi.getResults(report.analysis_job_id);
                        setAnalysisData(analysis);

                        // Fetch imagery as blob URLs (authenticated)
                        const imageryKeys = analysis.results_json?.imagery_paths
                            ? Object.keys(analysis.results_json.imagery_paths)
                            : [];
                        if (imageryKeys.length > 0) {
                            const urls: Record<string, string> = {};
                            await Promise.all(
                                imageryKeys.map(async (key: string) => {
                                    const blobUrl = await analysisApi.getImageryBlobUrl(analysis.job_id, key);
                                    if (blobUrl) urls[key] = blobUrl;
                                })
                            );
                            setImageBlobUrls(urls);
                        }
                    } catch (analysisError) {
                        console.warn('Analysis data not available:', analysisError);
                    }
                }

                // 4. Skip live validation when snapshot exists — snapshot is the
                //    frozen source of truth and should not be supplemented with
                //    recalculated data that could diverge from the original report.
            } catch (err: any) {
                console.error('Error loading report data:', err);
                setError(getErrorMessage(err) || t('technicalReport.errorLoadingData'));
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();

        // Cleanup blob URLs on unmount
        return () => {
            Object.values(imageBlobUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, [reportId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-96">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">{t('technicalReport.loadingReport')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6">
                <div className="flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {t('technicalReport.errorLoadingReport')}
                        </h3>
                        <p className="text-red-600">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!reportData || !analysisData) {
        return null;
    }

    // Map API data to component structure
    const report = {
        certificate_id: reportData.certificate_id,
        entity_name: analysisData.polygon_name || t('technicalReport.unnamed'),
        polygon_id: analysisData.polygon_id,
        compliance_status: analysisData.compliance_status || 'unknown',
        risk_level: analysisData.risk_level || 'UNKNOWN',
        issue_date: reportData.created_at.split('T')[0],
        valid_until: reportData.expires_at ? reportData.expires_at.split('T')[0] : 'N/A',
    };

    const analysisParams: AnalysisParams = {
        satellite_platform: 'Sentinel-2 (ESA)',
        spatial_resolution: '10 meters',
        spectral_bands: ['B2 (Blue)', 'B3 (Green)', 'B4 (Red)', 'B8 (NIR)'],
        baseline_period: analysisData.baseline_period || '2020',
        monitoring_period: analysisData.monitoring_period || '2025',
        analysis_method: 'NDVI Differencing',
        cloud_cover_filter: analysisData.cloud_cover_max ? `< ${analysisData.cloud_cover_max}%` : '< 50%',
        ndvi_threshold: analysisData.threshold_used || 0.60,
        deforestation_threshold: analysisData.threshold_used || -0.15,
    };

    const metrics: StatisticalMetrics = {
        total_area_ha: analysisData.area_total_ha || 0,
        stable_forest_ha: (analysisData.area_total_ha || 0) - (analysisData.area_deforested_ha || 0),
        detected_loss_ha: analysisData.area_deforested_ha || 0,
        loss_rate_percent: analysisData.deforestation_percentage || 0,
        compliance_threshold_ha: 0.50,
        margin_to_threshold_ha: 0.50 - (analysisData.area_deforested_ha || 0),
    };

    // Check if real imagery is available (loaded via authenticated blob URLs)
    const hasRealImagery = Object.keys(imageBlobUrls).length > 0;

    const imagery: SatelliteImagery = {
        baseline: {
            url: imageBlobUrls['baseline_ndvi'] || '',
            date: `${analysisParams.baseline_period}-12-31`,
            cloud_cover: 12.5,
            scene_id: `Sentinel-2 Composite ${analysisParams.baseline_period}`,
        },
        monitoring: {
            url: imageBlobUrls['monitoring_ndvi'] || '',
            date: `${analysisParams.monitoring_period}-12-31`,
            cloud_cover: 8.3,
            scene_id: `Sentinel-2 Composite ${analysisParams.monitoring_period}`,
        },
        change_detection: {
            url: imageBlobUrls['change_detection'] || '',
            alerts_count: hasRealImagery ? Math.round((analysisData.area_deforested_ha || 0) / 0.1) : 0,
            largest_patch_ha: analysisData.area_deforested_ha || 0,
        },
    };

    const baseline: BaselineState | null = validationData?.baseline_state ? {
        classification: validationData.baseline_state.classification,
        forest_cover_percentage: validationData.baseline_state.forest_cover_percentage,
        is_locked: validationData.baseline_state.is_locked,
        reference_date: validationData.baseline_state.reference_date,
        confidence: validationData.baseline_state.confidence * 100,
        data_source: validationData.baseline_state.data_source,
    } : null;

    const faoThresholds: FAOThresholds | null = validationData?.fao_thresholds ? {
        tree_cover_density: {
            threshold: validationData.fao_thresholds.tree_cover_density.threshold,
            current_value: validationData.fao_thresholds.tree_cover_density.current_value,
            compliant: validationData.fao_thresholds.tree_cover_density.compliant,
        },
        minimum_area: {
            threshold_ha: validationData.fao_thresholds.minimum_area.threshold,
            current_ha: validationData.fao_thresholds.minimum_area.current_value,
            compliant: validationData.fao_thresholds.minimum_area.compliant,
        },
        tree_height: {
            threshold_m: validationData.fao_thresholds.tree_height.threshold,
            estimated_m: validationData.fao_thresholds.tree_height.current_value,
            compliant: validationData.fao_thresholds.tree_height.compliant,
            method: validationData.fao_thresholds.tree_height.method || 'N/A',
        },
    } : null;

    const edgeEffects: EdgeEffects | null = validationData?.edge_effects ? {
        original_area_ha: validationData.edge_effects.original_area_ha,
        buffer_distance_m: validationData.edge_effects.buffer_distance_m,
        core_area_ha: validationData.edge_effects.corrected_area_ha,
        edge_percentage: validationData.edge_effects.area_reduction_percentage,
    } : null;

    const tabs = [
        { id: 'imagery', label: t('technicalReport.tabs.imagery'), icon: Satellite },
        { id: 'statistics', label: t('technicalReport.tabs.statistics'), icon: BarChart3 },
        { id: 'eudr', label: t('technicalReport.tabs.eudr'), icon: Shield },
        { id: 'parameters', label: t('technicalReport.tabs.parameters'), icon: Activity },
        { id: 'metadata', label: t('technicalReport.tabs.metadata'), icon: Info },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="card p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <FileText className="w-8 h-8 text-primary-600" />
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {t('technicalReport.title')}
                            </h1>
                        </div>
                        <p className="text-gray-500">
                            {t('technicalReport.regulationReference')}
                        </p>
                    </div>
                    <button className="btn-primary flex items-center gap-2">
                        <Download className="w-5 h-5" />
                        {t('reports.downloadPDF')}
                    </button>
                </div>

                {/* Entity Info */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">{t('technicalReport.certifiedEntity')}</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                                {report.entity_name}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-1">{t('technicalReport.polygonReference')}</p>
                            <code className="text-sm bg-white dark:bg-gray-700 px-2 py-1 rounded">
                                {report.polygon_id}
                            </code>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-1">{t('technicalReport.analysisPeriod')}</p>
                            <p className="text-sm text-gray-900 dark:text-white">
                                {analysisParams.baseline_period} → {analysisParams.monitoring_period}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Compliance Status */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className={`rounded-lg p-4 border-2 ${
                        report.compliance_status === 'compliant'
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                            : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                    }`}>
                        <div className="flex items-center gap-2 mb-2">
                            {report.compliance_status === 'compliant' ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-600" />
                            )}
                            <span className={`font-semibold ${
                                report.compliance_status === 'compliant'
                                    ? 'text-green-900 dark:text-green-100'
                                    : 'text-red-900 dark:text-red-100'
                            }`}>
                                {report.compliance_status === 'compliant' ? t('technicalReport.compliant') : t('technicalReport.nonCompliant')}
                            </span>
                        </div>
                        <p className={`text-xs ${
                            report.compliance_status === 'compliant'
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-red-700 dark:text-red-300'
                        }`}>
                            {report.compliance_status === 'compliant'
                                ? t('technicalReport.deforestationFreeVerified')
                                : t('technicalReport.deforestationDetected')}
                        </p>
                    </div>

                    <div>
                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.forestLoss')}</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {metrics.detected_loss_ha.toFixed(2)} ha
                        </p>
                        <p className="text-xs text-gray-500">{metrics.loss_rate_percent}% {t('technicalReport.ofArea')}</p>
                    </div>

                    <div>
                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.riskLevel')}</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {report.risk_level}
                        </p>
                        <p className="text-xs text-gray-500">{t('technicalReport.classification')}</p>
                    </div>

                    <div>
                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.certificateId')}</p>
                        <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded block truncate">
                            {report.certificate_id}
                        </code>
                        <p className="text-xs text-gray-500 mt-1">
                            {t('technicalReport.validUntil')}: {report.valid_until}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="flex overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-primary-600 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                <tab.icon className="w-5 h-5" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6">
                    {/* Satellite Imagery Tab */}
                    {activeTab === 'imagery' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('technicalReport.imagery.ndviAnalysis')}
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    {t('technicalReport.imagery.ndviAnalysisDescription')}
                                </p>
                            </div>

                            {/* Show real imagery if available, otherwise show placeholder grid */}
                            {hasRealImagery ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Baseline NDVI */}
                                    <div className="space-y-3">
                                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                                            <img
                                                src={imagery.baseline.url}
                                                alt="Baseline NDVI"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                                                {t('technicalReport.imagery.ndviBaseline', { year: analysisParams.baseline_period })}
                                            </h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {t('technicalReport.imagery.ndviBaselineDescription')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Monitoring NDVI */}
                                    <div className="space-y-3">
                                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                                            <img
                                                src={imagery.monitoring.url}
                                                alt="Monitoring NDVI"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                                                {t('technicalReport.imagery.ndviMonitoring', { year: analysisParams.monitoring_period })}
                                            </h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {t('technicalReport.imagery.ndviMonitoringDescription')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Change Detection */}
                                    <div className="space-y-3">
                                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-red-300 dark:border-red-600">
                                            <img
                                                src={imagery.change_detection.url}
                                                alt="Change Detection"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                                                {t('technicalReport.imagery.changeMapTitle')}
                                            </h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {t('technicalReport.imagery.changeMapDescription')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Deforestation Mask */}
                                    <div className="space-y-3">
                                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-red-500 dark:border-red-700">
                                            <img
                                                src={imageBlobUrls['deforestation_mask'] || ''}
                                                alt="Deforestation Mask"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                                                {t('technicalReport.imagery.deforestationDetected')}
                                            </h4>
                                            <div className="text-sm space-y-1">
                                                <p className="text-gray-600 dark:text-gray-400 mb-2">
                                                    {t('technicalReport.imagery.deforestationMaskDescription')}
                                                </p>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">{t('technicalReport.imagery.totalDeforestedArea')}:</span>
                                                    <span className="font-semibold text-red-600">
                                                        {metrics.detected_loss_ha.toFixed(4)} ha
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">{t('technicalReport.imagery.areaPercentage')}:</span>
                                                    <span className="font-semibold text-red-600">
                                                        {metrics.loss_rate_percent.toFixed(2)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-8 text-center">
                                    <Satellite className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                        Satellite Imagery Not Available
                                    </h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                                        Imagery data was not generated for this analysis, or the files are no longer
                                        accessible. The statistical results and compliance assessment below remain valid.
                                    </p>
                                    <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
                                        <div>
                                            <span className="block font-medium">Baseline</span>
                                            <span className="text-xs">{analysisParams.baseline_period}</span>
                                        </div>
                                        <div>
                                            <span className="block font-medium">Monitoring</span>
                                            <span className="text-xs">{analysisParams.monitoring_period}</span>
                                        </div>
                                        <div>
                                            <span className="block font-medium">Loss Detected</span>
                                            <span className="text-xs">{metrics.detected_loss_ha.toFixed(4)} ha</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* NDVI Scale Legend */}
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                                    {t('technicalReport.imagery.ndviScale')}
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div>
                                        <div className="w-full h-3 bg-gradient-to-r from-green-800 to-green-600 rounded mb-1"></div>
                                        <p className="text-gray-500">
                                            <span className="font-semibold">0.6 - 1.0:</span> {t('technicalReport.imagery.denseForest')}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="w-full h-3 bg-gradient-to-r from-green-400 to-yellow-400 rounded mb-1"></div>
                                        <p className="text-gray-500">
                                            <span className="font-semibold">0.3 - 0.6:</span> {t('technicalReport.imagery.vegetation')}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="w-full h-3 bg-gradient-to-r from-yellow-600 to-orange-600 rounded mb-1"></div>
                                        <p className="text-gray-500">
                                            <span className="font-semibold">0.1 - 0.3:</span> {t('technicalReport.imagery.sparseVeg')}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="w-full h-3 bg-gradient-to-r from-orange-400 to-red-600 rounded mb-1"></div>
                                        <p className="text-gray-500">
                                            <span className="font-semibold">&lt; 0.1:</span> {t('technicalReport.imagery.bareSoil')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Statistical Analysis Tab */}
                    {activeTab === 'statistics' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('technicalReport.statistics.title')}
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    {t('technicalReport.statistics.description')}
                                </p>
                            </div>

                            {/* Compliance Threshold Visualization */}
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-6">
                                    {t('technicalReport.statistics.complianceThresholdAnalysis')}
                                </h4>
                                {(() => {
                                    const detectedLossPercent = (metrics.detected_loss_ha / metrics.total_area_ha) * 100;
                                    const thresholdPercent = (metrics.compliance_threshold_ha / metrics.total_area_ha) * 100;
                                    const excessPercent = detectedLossPercent - thresholdPercent;
                                    const isCompliant = metrics.margin_to_threshold_ha >= 0;

                                    // Scale so the larger value sits at ~70% of the bar
                                    const maxVal = Math.max(detectedLossPercent, thresholdPercent, 0.01);
                                    const scale = 70 / maxVal;
                                    const lossPos = Math.min(detectedLossPercent * scale, 95);
                                    const thresholdPos = Math.min(thresholdPercent * scale, 95);

                                    return (
                                        <div className="space-y-3">
                                            {/* Legend chips */}
                                            <div className="flex flex-wrap items-center gap-3 text-xs mb-2">
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-medium">
                                                    <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>
                                                    {t('technicalReport.statistics.eudrLimit')}: {thresholdPercent.toFixed(2)}% ({metrics.compliance_threshold_ha.toFixed(2)} ha)
                                                </span>
                                                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded font-medium ${
                                                    isCompliant
                                                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                                                        : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                                                }`}>
                                                    <span className={`w-2 h-2 rounded-full inline-block ${isCompliant ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                                    {t('technicalReport.statistics.detectedLoss')}: {detectedLossPercent.toFixed(2)}% ({metrics.detected_loss_ha.toFixed(2)} ha)
                                                </span>
                                            </div>

                                            {/* Bar visualization */}
                                            <div className="relative pt-8 pb-8">
                                                {/* Background bar */}
                                                <div className="relative h-10 bg-gradient-to-r from-green-200 via-yellow-100 to-red-200 dark:from-green-900/40 dark:via-yellow-900/30 dark:to-red-900/40 rounded-lg">
                                                    {/* Green compliant zone fill */}
                                                    <div
                                                        className="absolute top-0 left-0 h-full bg-green-300/50 dark:bg-green-700/30 rounded-l-lg"
                                                        style={{ width: `${thresholdPos}%` }}
                                                    ></div>

                                                    {/* Excess zone (red overlay between threshold and loss) */}
                                                    {!isCompliant && (
                                                        <div
                                                            className="absolute top-0 h-full bg-red-400/30 dark:bg-red-600/30"
                                                            style={{
                                                                left: `${thresholdPos}%`,
                                                                width: `${lossPos - thresholdPos}%`,
                                                            }}
                                                        >
                                                            <div className="flex items-center justify-center h-full text-[10px] font-bold text-red-700 dark:text-red-300 whitespace-nowrap">
                                                                +{Math.abs(excessPercent).toFixed(2)}%
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Threshold marker (dashed amber line) */}
                                                    <div
                                                        className="absolute top-0 h-full z-10"
                                                        style={{ left: `${thresholdPos}%` }}
                                                    >
                                                        <div className="h-full w-0.5 border-l-2 border-dashed border-amber-500"></div>
                                                        <div className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap font-semibold">
                                                            {t('technicalReport.statistics.limit')}: {thresholdPercent.toFixed(2)}%
                                                        </div>
                                                    </div>

                                                    {/* Loss marker (solid line with triangle) */}
                                                    <div
                                                        className="absolute top-0 h-full z-20"
                                                        style={{ left: `${lossPos}%` }}
                                                    >
                                                        <div className={`h-full w-0.5 ${isCompliant ? 'bg-green-600' : 'bg-red-600'}`}></div>
                                                        {/* Triangle above */}
                                                        <div className={`absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent ${
                                                            isCompliant ? 'border-t-green-600' : 'border-t-red-600'
                                                        }`}></div>
                                                        {/* Label above */}
                                                        <div className={`absolute bottom-full mb-3 left-1/2 -translate-x-1/2 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap font-semibold ${
                                                            isCompliant ? 'bg-green-600' : 'bg-red-600'
                                                        }`}>
                                                            {isCompliant ? '✓' : '✗'} {t('technicalReport.statistics.loss')}: {detectedLossPercent.toFixed(2)}%
                                                        </div>
                                                    </div>

                                                    {/* 0% label */}
                                                    <div className="absolute left-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400 font-mono">0%</div>
                                                </div>
                                            </div>

                                            {/* Zone labels */}
                                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    <span className="w-3 h-1.5 rounded-sm bg-green-300 dark:bg-green-700 inline-block"></span>
                                                    {t('technicalReport.statistics.compliantZone')}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <span className="w-3 h-1.5 rounded-sm bg-red-300 dark:bg-red-700 inline-block"></span>
                                                    {t('technicalReport.statistics.nonCompliantZone')}
                                                </span>
                                            </div>

                                            {/* Summary text */}
                                            <p className={`text-sm mt-2 font-semibold ${
                                                isCompliant
                                                    ? 'text-green-600 dark:text-green-400'
                                                    : 'text-red-600 dark:text-red-400'
                                            }`}>
                                                {isCompliant ? '✓' : '✗'} {t('technicalReport.statistics.detectedLoss')} ({detectedLossPercent.toFixed(2)}% / {metrics.detected_loss_ha.toFixed(2)} ha) {t('technicalReport.statistics.is')} {Math.abs(excessPercent).toFixed(2)}% {isCompliant ? t('technicalReport.statistics.below') : t('technicalReport.statistics.above')} {t('technicalReport.statistics.theLimit')} {thresholdPercent.toFixed(2)}% ({metrics.compliance_threshold_ha.toFixed(2)} ha)
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Detailed Measurements Table */}
                            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                                <table className="w-full min-w-[560px]">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                {t('technicalReport.statistics.metric')}
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                {t('technicalReport.statistics.hectares')}
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                {t('technicalReport.statistics.squareMeters')}
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                {t('technicalReport.statistics.percentage')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        <tr className="bg-white dark:bg-gray-900">
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {t('technicalReport.statistics.totalAnalyzedArea')}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono">
                                                {metrics.total_area_ha.toFixed(4)} ha
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {(metrics.total_area_ha * 10000).toFixed(0)} m²
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono">
                                                100.00%
                                            </td>
                                        </tr>
                                        <tr className="bg-gray-50 dark:bg-gray-800/30">
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {t('technicalReport.statistics.stableForestCover')}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-900 dark:text-white">
                                                {metrics.stable_forest_ha.toFixed(4)} ha
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {(metrics.stable_forest_ha * 10000).toFixed(0)} m²
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-900 dark:text-white">
                                                {((metrics.stable_forest_ha / metrics.total_area_ha) * 100).toFixed(2)}%
                                            </td>
                                        </tr>
                                        <tr className="bg-gray-100 dark:bg-gray-800/50">
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-semibold">
                                                {t('technicalReport.statistics.detectedForestLoss')}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono font-semibold text-gray-900 dark:text-white">
                                                {metrics.detected_loss_ha.toFixed(4)} ha
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {(metrics.detected_loss_ha * 10000).toFixed(0)} m²
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono font-semibold text-gray-900 dark:text-white">
                                                {metrics.loss_rate_percent.toFixed(2)}%
                                            </td>
                                        </tr>
                                        <tr className="bg-gray-50 dark:bg-gray-800/30">
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {t('technicalReport.statistics.complianceThreshold')}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-900 dark:text-white">
                                                {metrics.compliance_threshold_ha.toFixed(4)} ha
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {(metrics.compliance_threshold_ha * 10000).toFixed(0)} m²
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {t('technicalReport.statistics.reference')}
                                            </td>
                                        </tr>
                                        <tr className="bg-gray-50 dark:bg-gray-800/30">
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {t('technicalReport.statistics.marginToThreshold')}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-900 dark:text-white">
                                                {metrics.margin_to_threshold_ha.toFixed(4)} ha
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {(metrics.margin_to_threshold_ha * 10000).toFixed(0)} m²
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-mono text-gray-500">
                                                {t('technicalReport.statistics.buffer')}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Methodology Notes */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                <div className="flex gap-3">
                                    <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        <p className="font-semibold mb-1">{t('technicalReport.statistics.methodologyNotes')}</p>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {t('technicalReport.statistics.methodologyDescription')}
                                        </p>
                                        <p className="mt-2 text-gray-600 dark:text-gray-300">
                                            {t('technicalReport.statistics.toleranceThreshold')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* EUDR Compliance Tab */}
                    {activeTab === 'eudr' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('technicalReport.eudr.title')}
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    {t('technicalReport.eudr.description')}
                                </p>
                            </div>

                            {!validationData && (
                                <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                    <div className="flex gap-3">
                                        <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-gray-900 dark:text-white">
                                            <p className="font-semibold mb-1">{t('technicalReport.eudr.validationNotAvailable')}</p>
                                            <p className="text-gray-600 dark:text-gray-300">
                                                {t('technicalReport.eudr.validationNotAvailableDescription')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Baseline State Section */}
                            {baseline && (
                            <div
                                className={`p-6 rounded-lg border-2 ${
                                    baseline.is_locked
                                        ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                                        : 'bg-green-50 dark:bg-green-900/20 border-green-500'
                                }`}
                            >
                                <div className="flex items-start gap-4">
                                    {baseline.is_locked ? (
                                        <Lock className="w-8 h-8 text-red-600 flex-shrink-0" />
                                    ) : (
                                        <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                                    )}
                                    <div className="flex-1">
                                        <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                            {t('technicalReport.eudr.baselineState')}
                                        </h4>
                                        <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                            {t('technicalReport.eudr.classification')}: {baseline.classification}
                                        </p>
                                        <p
                                            className={`text-sm mb-4 ${
                                                baseline.is_locked
                                                    ? 'text-red-700 dark:text-red-300'
                                                    : 'text-green-700 dark:text-green-300'
                                            }`}
                                        >
                                            {baseline.is_locked
                                                ? t('technicalReport.eudr.lockedDescription')
                                                : t('technicalReport.eudr.safeDescription')}
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-gray-500 mb-1">{t('technicalReport.eudr.forestCover')}</p>
                                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {baseline.forest_cover_percentage.toFixed(1)}%
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-gray-500 mb-1">{t('technicalReport.eudr.confidence')}</p>
                                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {baseline.confidence.toFixed(1)}%
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-gray-500 mb-1">{t('technicalReport.eudr.referenceDate')}</p>
                                                <p className="font-mono text-sm text-gray-900 dark:text-white">
                                                    {baseline.reference_date}
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-gray-500 mb-1">{t('technicalReport.eudr.dataSource')}</p>
                                                <p className="text-xs text-gray-900 dark:text-white">
                                                    {baseline.data_source}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* FAO Thresholds Section */}
                            {faoThresholds && (
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    {t('technicalReport.eudr.faoThresholdsTitle')}
                                </h4>
                                <p className="text-sm text-gray-500 mb-4">
                                    {t('technicalReport.eudr.faoThresholdsDescription')}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Tree Cover Density */}
                                    <div className="border border-line dark:border-gray-700 rounded-lg p-5 bg-gray-50 dark:bg-gray-800/50">
                                        <div className="flex items-center gap-2 mb-3">
                                            <TreePine className="w-6 h-6 text-gray-500" />
                                            <h5 className="font-semibold text-gray-900 dark:text-white">
                                                {t('technicalReport.eudr.treeCoverDensity')}
                                            </h5>
                                        </div>
                                        <div className="space-y-2">
                                            <div>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.threshold')}</p>
                                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                                    &gt; {faoThresholds.tree_cover_density.threshold}%
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.current')}</p>
                                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {faoThresholds.tree_cover_density.current_value}%
                                                </p>
                                            </div>
                                            <div
                                                className={`flex items-center gap-1 text-sm font-medium ${
                                                    faoThresholds.tree_cover_density.compliant
                                                        ? 'text-green-700 dark:text-green-300'
                                                        : 'text-red-700 dark:text-red-300'
                                                }`}
                                            >
                                                {faoThresholds.tree_cover_density.compliant ? (
                                                    <CheckCircle className="w-4 h-4" />
                                                ) : (
                                                    <XCircle className="w-4 h-4" />
                                                )}
                                                {faoThresholds.tree_cover_density.compliant ? t('technicalReport.eudr.pass') : t('technicalReport.eudr.fail')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Minimum Area */}
                                    <div className="border border-line dark:border-gray-700 rounded-lg p-5 bg-gray-50 dark:bg-gray-800/50">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Layers className="w-6 h-6 text-gray-500" />
                                            <h5 className="font-semibold text-gray-900 dark:text-white">
                                                {t('technicalReport.eudr.minimumArea')}
                                            </h5>
                                        </div>
                                        <div className="space-y-2">
                                            <div>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.threshold')}</p>
                                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                                    &gt; {faoThresholds.minimum_area.threshold_ha} ha
                                                </p>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.approxPixels')}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.current')}</p>
                                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {faoThresholds.minimum_area.current_ha} ha
                                                </p>
                                            </div>
                                            <div
                                                className={`flex items-center gap-1 text-sm font-medium ${
                                                    faoThresholds.minimum_area.compliant
                                                        ? 'text-green-700 dark:text-green-300'
                                                        : 'text-red-700 dark:text-red-300'
                                                }`}
                                            >
                                                {faoThresholds.minimum_area.compliant ? (
                                                    <CheckCircle className="w-4 h-4" />
                                                ) : (
                                                    <XCircle className="w-4 h-4" />
                                                )}
                                                {faoThresholds.minimum_area.compliant ? t('technicalReport.eudr.pass') : t('technicalReport.eudr.fail')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tree Height */}
                                    <div className="border border-line dark:border-gray-700 rounded-lg p-5 bg-gray-50 dark:bg-gray-800/50">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Zap className="w-6 h-6 text-gray-500" />
                                            <h5 className="font-semibold text-gray-900 dark:text-white">
                                                {t('technicalReport.eudr.treeHeight')}
                                            </h5>
                                        </div>
                                        <div className="space-y-2">
                                            <div>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.threshold')}</p>
                                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                                    &gt; {faoThresholds.tree_height.threshold_m} m
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">{t('technicalReport.eudr.estimated')}</p>
                                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {faoThresholds.tree_height.estimated_m} m
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {faoThresholds.tree_height.method}
                                                </p>
                                            </div>
                                            <div
                                                className={`flex items-center gap-1 text-sm font-medium ${
                                                    faoThresholds.tree_height.compliant
                                                        ? 'text-green-700 dark:text-green-300'
                                                        : 'text-red-700 dark:text-red-300'
                                                }`}
                                            >
                                                {faoThresholds.tree_height.compliant ? (
                                                    <CheckCircle className="w-4 h-4" />
                                                ) : (
                                                    <XCircle className="w-4 h-4" />
                                                )}
                                                {faoThresholds.tree_height.compliant ? t('technicalReport.eudr.pass') : t('technicalReport.eudr.fail')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Edge Effects Section */}
                            {edgeEffects && (
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-6">
                                <div className="flex items-start gap-4">
                                    <Shield className="w-8 h-8 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1">
                                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                            {t('technicalReport.eudr.edgeEffectsCorrection')}
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                            {t('technicalReport.eudr.edgeEffectsDescription')}
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-xs text-gray-500 mb-1">{t('technicalReport.eudr.originalArea')}</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {edgeEffects.original_area_ha.toFixed(2)} ha
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-xs text-gray-500 mb-1">{t('technicalReport.eudr.coreArea')}</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {edgeEffects.core_area_ha.toFixed(2)} ha
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-xs text-gray-500 mb-1">{t('technicalReport.eudr.buffer')}</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {edgeEffects.buffer_distance_m} m
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded">
                                                <p className="text-xs text-gray-500 mb-1">{t('technicalReport.eudr.edgePercentage')}</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {edgeEffects.edge_percentage.toFixed(1)}%
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* EUDR Logic Explanation */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                <div className="flex gap-3">
                                    <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        <p className="font-semibold mb-2">{t('technicalReport.eudr.baselineLogicTitle')}</p>
                                        <div className="space-y-2 text-gray-600 dark:text-gray-300">
                                            <p>
                                                <strong>IF State_Initial == FOREST:</strong> {t('technicalReport.eudr.forestLogic')}
                                            </p>
                                            <p>
                                                <strong>IF State_Initial == AGRICULTURE/GRASSLAND:</strong> {t('technicalReport.eudr.agricultureLogic')}
                                            </p>
                                            <p className="mt-3">
                                                <strong>{t('technicalReport.eudr.edgeEffectsRationale')}:</strong> {t('technicalReport.eudr.edgeEffectsRationaleDescription')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Warning for Height Measurement */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                <div className="flex gap-3">
                                    <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        <p className="font-semibold mb-1">{t('technicalReport.eudr.heightLimitationTitle')}</p>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {t('technicalReport.eudr.heightLimitationDescription')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Analysis Parameters Tab */}
                    {activeTab === 'parameters' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('technicalReport.parameters.title')}
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    {t('technicalReport.parameters.description')}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Satellite Configuration */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Satellite className="w-5 h-5 text-primary-600" />
                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {t('technicalReport.parameters.satellitePlatform')}
                                        </h4>
                                    </div>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.platform')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">
                                                {analysisParams.satellite_platform}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">Copernicus Programme</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.spatialResolution')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">
                                                {analysisParams.spatial_resolution}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.spectralBands')}</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {analysisParams.spectral_bands.map((band) => (
                                                    <span
                                                        key={band}
                                                        className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2 py-1 rounded"
                                                    >
                                                        {band}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.cloudCoverFilter')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">
                                                {analysisParams.cloud_cover_filter}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">Quality assurance threshold</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Temporal Configuration */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Calendar className="w-5 h-5 text-primary-600" />
                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {t('technicalReport.parameters.temporalAnalysis')}
                                        </h4>
                                    </div>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.baselinePeriod')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">
                                                {analysisParams.baseline_period}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Reference date &gt; Dec 31, 2020
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.monitoringPeriod')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">
                                                {analysisParams.monitoring_period}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">Current assessment</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.analysisMethod')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">
                                                {analysisParams.analysis_method}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">Spectral change detection</p>
                                        </div>
                                    </div>
                                </div>

                                {/* NDVI Thresholds */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Activity className="w-5 h-5 text-primary-600" />
                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {t('technicalReport.parameters.ndviThresholds')}
                                        </h4>
                                    </div>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.forestNdviThreshold')}</p>
                                            <p className="font-mono text-2xl text-gray-900 dark:text-white">
                                                {analysisParams.ndvi_threshold.toFixed(2)}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Minimum NDVI value for forest classification
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.deforestationThreshold')}</p>
                                            <p className="font-mono text-2xl text-gray-900 dark:text-white">
                                                {analysisParams.deforestation_threshold.toFixed(2)}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                NDVI change indicating deforestation
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Compliance Configuration */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <CheckCircle className="w-5 h-5 text-primary-600" />
                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {t('technicalReport.parameters.complianceParameters')}
                                        </h4>
                                    </div>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.thresholdLimit')}</p>
                                            <p className="font-mono text-2xl text-gray-900 dark:text-white">
                                                {metrics.compliance_threshold_ha.toFixed(2)} ha
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Maximum allowable forest loss
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">{t('technicalReport.parameters.minimumPatchSize')}</p>
                                            <p className="font-mono text-gray-900 dark:text-white">0.1 ha</p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Minimum detectable change unit
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Metadata Tab */}
                    {activeTab === 'metadata' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('technicalReport.metadata.title')}
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    {t('technicalReport.metadata.description')}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.certificateId')}</p>
                                        <code className="text-sm bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded block break-all">
                                            {report.certificate_id}
                                        </code>
                                    </div>

                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.metadata.documentHash')}</p>
                                        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded block break-all font-mono">
                                            89E5C5EA3A780B83E4A270A319529607
                                        </code>
                                    </div>

                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.metadata.generationTimestamp')}</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-mono">
                                            2025-12-02 01:11:50 UTC
                                        </p>
                                    </div>

                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.metadata.issuingAuthority')}</p>
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            EUDR Sentinel Monitor
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.metadata.certificationDate')}</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-mono">
                                            {report.issue_date}
                                        </p>
                                    </div>

                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.validUntil')}</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-mono">
                                            {report.valid_until}
                                        </p>
                                    </div>

                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.metadata.authorizedSignatory')}</p>
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            {t('technicalReport.metadata.automatedAnalysisSystem')}
                                        </p>
                                    </div>

                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('technicalReport.metadata.reportVersion')}</p>
                                        <p className="text-sm text-gray-900 dark:text-white">2.1.0 Enterprise</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                <div className="flex gap-3">
                                    <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        <p className="font-semibold mb-1">{t('technicalReport.metadata.legalDisclaimer')}</p>
                                        <p className="text-gray-600 dark:text-gray-300 text-xs">
                                            {t('technicalReport.metadata.disclaimerText1')}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-300 text-xs mt-2">
                                            {t('technicalReport.metadata.disclaimerText2')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
